import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { ContentLanguageService } from 'src/app/services/content-language.service';

export interface WellnessNotification {
  id: number;
  studentName: string;
  className: string;
  date: Date;
  read: boolean;
  signalType: string;
  roomId: string;
  studentId: string;
  trigger_translation: string | null;
}

export interface ApiNotification {
  notification_id: number;
  student_name: string;
  room_name: string;
  created_at: string;
  read: boolean;
  signal_type: string;
  room_id: string;
  student_id: string;
  trigger_translation: string | null;
}

/** Item del nuevo endpoint `GET /notifications/recipients/` — alertas para
 *  la pantalla de Notificaciones del docente/orientador. */
export interface NotificationRecipient {
  notification_id: number;
  read: boolean;
  student_id: number;
  student_name: string;
  room_id: number;
  course_name: string;
  signal_type: string;
  created_at: string;
}

/** Lectura generada por IA para el panel "Nolia AI" de Notificaciones.
 *  Mismo contrato que `SociogramInsight`: sólo `body` e `items` son dinámicos;
 *  las etiquetas fijas (badge, título, sección, CTA) las pone el i18n. */
export interface NotificationInsight {
  /** Texto principal — soporta HTML enriquecido. */
  body: string;
  /** Acciones / observaciones sugeridas — cada item soporta HTML enriquecido. */
  items: string[];
}

/** Respuesta envoltorio de `GET /notifications/recipients/`. */
export interface NotificationRecipientsResponse {
  unread_count: number;
  year_total: number;
  results: NotificationRecipient[];
  /** Lectura de IA del grupo; puede no venir si el backend todavía no la calculó. */
  insight?: NotificationInsight;
}

@Injectable({
  providedIn: 'root'
})
export class WellnessNotificationService {
  private notifications$ = new BehaviorSubject<WellnessNotification[]>([]);
  private unreadCount$ = new BehaviorSubject<number>(0);

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
    private contentLanguageService: ContentLanguageService
  ) {}

  private getRequestHeaders() {
    return {
      headers: {
        Authorization: 'Bearer ' + this.cookieService.get('gr_access')
      }
    };
  }

  getNotifications(): Observable<WellnessNotification[]> {
    console.log('getNotifications called, current notifications:', this.notifications$.value);
    return this.notifications$.asObservable();
  }

  getUnreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  setNotificationsFromApi(apiNotifications: ApiNotification[], unreadCount: number): void {
    const notifications = apiNotifications.map(n => this.mapApiNotification(n));
    this.notifications$.next(notifications);
    this.unreadCount$.next(unreadCount);
  }

  markAsRead(notificationId: number): void {
    const notification = this.notifications$.value.find(n => n.id === notificationId);
    if (notification?.read) {
      return;
    }

    // Call API to mark as read
    this.httpClient.patch(
      `${environment.apiUrl}/notifications/recipients/${notificationId}/mark_as_read/`,
      {},
      this.getRequestHeaders()
    ).subscribe({
      next: () => {
        // Update local state on success
        const current = this.notifications$.value;
        const updated = current.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        );
        this.notifications$.next(updated);
        this.unreadCount$.next(updated.filter(n => !n.read).length);
      },
      error: (err) => {
        console.error('Error marking notification as read:', err);
      }
    });
  }

  /** Lista las alertas asignadas al usuario logueado, con contadores. Se
   *  envía `lang` para que el backend devuelva el `insight` de Nolia AI en
   *  el idioma activo (mismo criterio que el sociograma). */
  fetchRecipientsAlerts(): Observable<NotificationRecipientsResponse> {
    const params = new HttpParams().set(
      'lang',
      this.contentLanguageService.getContentLang(),
    );
    return this.httpClient.get<NotificationRecipientsResponse>(
      `${environment.apiUrl}/notifications/recipients/`,
      { ...this.getRequestHeaders(), params },
    );
  }

  /**
   * Marca como leída una alerta y devuelve el objeto actualizado. A
   * diferencia de `markAsRead`, este método retorna el observable para que
   * el consumidor maneje su propio estado (sin tocar el BehaviorSubject
   * interno del servicio).
   */
  markRecipientAsRead(
    notificationId: number,
  ): Observable<NotificationRecipient> {
    return this.httpClient.patch<NotificationRecipient>(
      `${environment.apiUrl}/notifications/recipients/${notificationId}/mark_as_read/`,
      {},
      this.getRequestHeaders(),
    );
  }

  markAllAsRead(): void {
    const current = this.notifications$.value;
    const updated = current.map(n => ({ ...n, read: true }));
    this.notifications$.next(updated);
    this.unreadCount$.next(0);
  }

  private mapApiNotification(api: ApiNotification): WellnessNotification {
    const [day, month, year] = api.created_at.split('-').map(Number);
    return {
      id: api.notification_id,
      studentName: api.student_name,
      className: api.room_name,
      date: new Date(year, month - 1, day),
      read: api.read,
      signalType: api.signal_type,
      roomId: api.room_id,
      studentId: api.student_id,
      trigger_translation: api.trigger_translation
    };
  }
}
